import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  DataTable,
  Dialog,
  EmptyState,
  ErrorState,
  LoadingState,
  useToast,
} from '@/components/ui';
import {
  DEDUCTION_DECISIONS,
  deductionDecisionLabel,
  type DeductionDecision,
} from '@/features/admin-v2/claim-status';
import { formatRM } from '@/features/billing/money';
import { isApiError } from '@/lib/api';
import {
  useDecidePanelReceivableAllocation,
  useKnockOffPanelReceivable,
  usePanelReceivableBatch,
  type PanelReceivableClaim,
} from './api';
import { AgeingIndicator } from './AgeingIndicator';
import { FollowUpActivityLog } from './FollowUpActivityLog';
import { LogFollowUpDrawer } from './LogFollowUpDrawer';
import styles from './PanelReceivables.module.css';

const visitDate = new Intl.DateTimeFormat('en-MY', { dateStyle: 'medium' });

export function BatchPatientDetailDrawer({
  batchId,
  canManage,
  onClose,
}: {
  batchId: string;
  canManage: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const detail = usePanelReceivableBatch(batchId);
  const decideAllocation = useDecidePanelReceivableAllocation();
  const knockOff = useKnockOffPanelReceivable();
  const [loggingFollowUp, setLoggingFollowUp] = useState(false);
  const batch = detail.data;
  const operationError = decideAllocation.error ?? knockOff.error;
  const apiError = isApiError(operationError) ? operationError : undefined;

  const decide = (allocationId: string, decision: DeductionDecision) => {
    decideAllocation.mutate(
      { batchId, allocationId, decision },
      {
        onSuccess: () =>
          toast.show({
            tone: 'success',
            title: t('panelReceivables.allocationUpdated'),
          }),
      },
    );
  };

  const applyRemittance = (remittanceId: string) => {
    if (!batch || batch.variance !== 0) return;
    knockOff.mutate(
      { batchId, remittanceId },
      {
        onSuccess: () =>
          toast.show({
            tone: 'success',
            title: t('panelReceivables.knockedOff'),
          }),
      },
    );
  };

  return (
    <>
      <Dialog
        open
        wide
        title={
          batch
            ? t('panelReceivables.batchTitle', { reference: batch.reference })
            : t('panelReceivables.batchDetails')
        }
        onClose={onClose}
        footer={<Button onClick={onClose}>{t('common.close')}</Button>}
      >
        {detail.isLoading && <LoadingState />}
        {detail.error && (
          <ErrorState
            error={detail.error}
            onRetry={() => void detail.refetch()}
          />
        )}
        {batch && (
          <div className={styles.drawerStack}>
            <dl className={styles.summary}>
              <div>
                <dt>{t('panelReceivables.panel')}</dt>
                <dd>{batch.panelName}</dd>
              </div>
              <div>
                <dt>{t('panelReceivables.period')}</dt>
                <dd>{batch.periodLabel}</dd>
              </div>
              <div>
                <dt>{t('panelReceivables.remittanceTotal')}</dt>
                <dd>{formatRM(batch.remittanceTotal)}</dd>
              </div>
              <div>
                <dt>{t('panelReceivables.allocatedTotal')}</dt>
                <dd>{formatRM(batch.allocatedTotal)}</dd>
              </div>
              <div>
                <dt>{t('panelReceivables.outstandingTotal')}</dt>
                <dd>{formatRM(batch.outstandingTotal)}</dd>
              </div>
              <div>
                <dt>{t('panelReceivables.variance')}</dt>
                <dd
                  className={batch.variance !== 0 ? styles.variance : undefined}
                >
                  {formatRM(batch.variance)}
                </dd>
              </div>
              <div>
                <dt>{t('panelReceivables.ageingLabel')}</dt>
                <dd>
                  <AgeingIndicator
                    bucket={batch.ageingBucket}
                    days={batch.ageingDays}
                  />
                </dd>
              </div>
            </dl>

            <section>
              <h3>{t('panelReceivables.patientClaims')}</h3>
              <DataTable<PanelReceivableClaim>
                caption={t('panelReceivables.patientClaims')}
                rows={batch.claims}
                rowKey={(claim) => claim.id}
                empty={
                  <EmptyState
                    title={t('panelReceivables.noClaims')}
                    body={t('panelReceivables.noClaimsBody')}
                  />
                }
                columns={[
                  {
                    key: 'patient',
                    header: t('panelReceivables.patient'),
                    render: (claim) => (
                      <div>
                        <strong>{claim.patientName}</strong>
                        <span className={styles.muted}>
                          {claim.claimReference}
                        </span>
                      </div>
                    ),
                  },
                  {
                    key: 'visit',
                    header: t('panelReceivables.visitDate'),
                    render: (claim) =>
                      visitDate.format(new Date(claim.visitDate)),
                  },
                  {
                    key: 'status',
                    header: t('panelReceivables.state'),
                    render: (claim) => <Badge>{claim.status}</Badge>,
                  },
                  {
                    key: 'claimed',
                    header: t('panelReceivables.claimed'),
                    align: 'right',
                    render: (claim) => formatRM(claim.claimedAmountSen),
                  },
                  {
                    key: 'approved',
                    header: t('panelReceivables.approved'),
                    align: 'right',
                    render: (claim) =>
                      claim.approvedAmountSen === null
                        ? t('panelReceivables.pending')
                        : formatRM(claim.approvedAmountSen),
                  },
                  {
                    key: 'received',
                    header: t('panelReceivables.received'),
                    align: 'right',
                    render: (claim) => formatRM(claim.receivedAmountSen),
                  },
                  {
                    key: 'outstanding',
                    header: t('panelReceivables.outstanding'),
                    align: 'right',
                    render: (claim) => (
                      <strong>{formatRM(claim.outstandingSen)}</strong>
                    ),
                  },
                ]}
              />
            </section>

            {batch.remittances.length > 0 && (
              <section>
                <h3>{t('panelReceivables.remittanceMatching')}</h3>
                <div className={styles.remittanceList}>
                  {batch.remittances.map((remittance) => (
                    <article className={styles.remittance} key={remittance.id}>
                      <div className={styles.sectionHeading}>
                        <div>
                          <strong>{remittance.reference}</strong>
                          <span className={styles.muted}>
                            {formatRM(remittance.amountSen)}
                          </span>
                        </div>
                        <Badge>{remittance.status}</Badge>
                      </div>
                      {remittance.allocations.map((allocation) => (
                        <div className={styles.allocation} key={allocation.id}>
                          <span>
                            {t('panelReceivables.allocationAmount', {
                              amount: formatRM(allocation.paidSen),
                            })}
                          </span>
                          {allocation.deductionSen > 0 &&
                            !allocation.decision &&
                            canManage && (
                              <div className={styles.actions}>
                                {DEDUCTION_DECISIONS.map(({ value, label }) => (
                                  <Button
                                    key={value}
                                    size="sm"
                                    variant={
                                      value === 'dispute' ? 'danger' : undefined
                                    }
                                    loading={
                                      decideAllocation.isPending &&
                                      decideAllocation.variables
                                        ?.allocationId === allocation.id &&
                                      decideAllocation.variables?.decision ===
                                        value
                                    }
                                    onClick={() => decide(allocation.id, value)}
                                  >
                                    {label}
                                  </Button>
                                ))}
                              </div>
                            )}
                          {allocation.decision && (
                            <Badge>
                              {deductionDecisionLabel(
                                allocation.decision,
                                remittance.status === 'applied',
                              )}
                            </Badge>
                          )}
                        </div>
                      ))}
                      {canManage && remittance.status !== 'applied' && (
                        <Button
                          variant="primary"
                          size="sm"
                          disabled={batch.variance !== 0}
                          loading={
                            knockOff.isPending &&
                            knockOff.variables?.remittanceId === remittance.id
                          }
                          onClick={() => applyRemittance(remittance.id)}
                        >
                          {t('panelReceivables.confirmKnockOff')}
                        </Button>
                      )}
                    </article>
                  ))}
                </div>
                {batch.variance !== 0 && (
                  <p className={styles.varianceNotice}>
                    {t('panelReceivables.varianceBlocksClosure')}
                  </p>
                )}
              </section>
            )}

            {apiError && <ErrorState error={apiError} />}

            <FollowUpActivityLog
              entries={batch.followUps}
              canManage={canManage}
              onLog={() => setLoggingFollowUp(true)}
            />
          </div>
        )}
      </Dialog>
      {loggingFollowUp && (
        <LogFollowUpDrawer
          batchId={batchId}
          onClose={() => setLoggingFollowUp(false)}
        />
      )}
    </>
  );
}
